import { HEALTH_HEALTHY, STATUS_RUNNING } from "@stores/instances/constants";
import {
  HOSTING_FEATURE_CLICKHOUSE_DWH,
  HOSTING_FEATURE_CUSTOM_DOMAIN,
  HOSTING_FEATURE_IP_ALLOWLIST,
  HOSTING_FEATURE_METABASE_AI,
  HOSTING_FEATURE_TRIAL_UP,
  METER_PRODUCT_ANALYTICS_EVENTS_KEY,
  METER_TRANSFORMS_ADVANCED_KEY,
  METER_TRANSFORMS_BASIC_KEY,
  SUBSCRIPTION_STATUS_ACTIVE,
  SUBSCRIPTION_STATUS_CANCELLATION_PENDING,
  SUBSCRIPTION_STATUS_CANCELLED,
  SUBSCRIPTION_STATUS_TRIAL,
} from "@stores/subscriptions/constants";
import {
  CreateInstanceDataType,
  IInstanceSetupData,
  IPurchase,
  ICreatePurchaseAddOn,
  IPurchaseTrialUp,
  IQuota,
  IMeterKey,
  IMeter,
} from "./types";
import {
  isDevPlan,
  isEnterprisePlan,
  isProPlan,
  isStarterPlan,
} from "@common/utils/plan";
import { captureException } from "@sentry/nextjs";
import {
  ADDON_PRODUCT_AI_PREPAID_UNITS_DEFAULT,
  ADDON_PRODUCT_DWH_PREPAID_UNITS_DEFAULT,
  ADDON_PRODUCT_DWH_PREPAID_UNITS_MIN,
  ADDON_PRODUCT_DWH_RENT_TYPE,
  ADDON_PRODUCT_DWH_TYPE,
  ADDON_PRODUCT_ETL_CONNECTIONS_TYPE,
  ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
  ADDON_PRODUCT_METABASE_AI_TYPE,
  ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE,
  ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE,
  ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE,
} from "@stores/addons/constants";
import {
  areConnectionsAllowed,
  hasFreeMetabaseAI,
  hasMetabaseAI,
  offerMeteredTransforms,
} from "@stores/config/utils";
import { IConfig } from "@stores/config/types";
import {
  getAnyProductAnalyticsAddOn,
  getAnyStorageAddOn,
  getPayAsYouGoMeterValues,
} from "@stores/subscriptions/utils";
import {
  getProductAnalyticsPricePer1M,
  getProductAnalyticsPricePerEvent,
} from "@stores/addons/utils";
import { buildPlanAlias } from "@stores/plans/utils";
import { AddOnProductType } from "@stores/addons/types";
import { PlanAliasType } from "@stores/plans/types";
import { PLAN_ALIAS_INTERNAL } from "@stores/plans/constants";

export function getCreateInstanceAddOnsData(
  {
    dwh,
    ai,
    aiFree,
    transforms,
  }: Pick<IInstanceSetupData, "dwh" | "ai" | "aiFree" | "transforms">,
  config: Pick<IConfig, "feature_flags">,
): ICreatePurchaseAddOn[] | undefined {
  const addOns: ICreatePurchaseAddOn[] = [];

  // DWH
  const isValidStoragePrepaidUnits = typeof dwh === "number";
  if (isValidStoragePrepaidUnits) {
    addOns.push({
      product_type: ADDON_PRODUCT_DWH_TYPE,
      prepaid_units:
        dwh < ADDON_PRODUCT_DWH_PREPAID_UNITS_MIN
          ? ADDON_PRODUCT_DWH_PREPAID_UNITS_DEFAULT
          : dwh,
    });
  }

  // ETL connections
  if (isValidStoragePrepaidUnits && areConnectionsAllowed(config)) {
    addOns.push({
      product_type: ADDON_PRODUCT_ETL_CONNECTIONS_TYPE,
      prepaid_units: 1,
    });
  }

  // Metabase AI
  const metabaseAI = hasMetabaseAI(config);
  const freeMetabaseAI = hasFreeMetabaseAI(config);
  if (ai && metabaseAI) {
    addOns.push({
      product_type: ADDON_PRODUCT_METABASE_AI_TIERED_TYPE,
      prepaid_units: ai,
    });
  }
  // Free metabot AI
  else if (!metabaseAI && aiFree && freeMetabaseAI) {
    addOns.push({
      product_type: ADDON_PRODUCT_METABASE_AI_TYPE,
      prepaid_units: ADDON_PRODUCT_AI_PREPAID_UNITS_DEFAULT,
    });
  }

  // Transforms
  if (transforms) {
    const offerMeteredTransformsFeatureFlag = offerMeteredTransforms(config);
    if (offerMeteredTransformsFeatureFlag) {
      addOns.push({
        product_type: transforms.includes("basic")
          ? ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE
          : ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE,
        prepaid_units: 1,
      });
    } else {
      addOns.push({
        product_type: transforms.includes("basic")
          ? ADDON_PRODUCT_TRANSFORMS_BASIC_TYPE
          : ADDON_PRODUCT_TRANSFORMS_ADVANCED_TYPE,
        prepaid_units: 1,
      });
    }
  }

  return addOns;
}

export function getProductAnalyticsUpsertAddOnsData(
  purchase: IPurchase,
  config: Pick<IConfig, "feature_flags">,
): ICreatePurchaseAddOn[] {
  const addOns: ICreatePurchaseAddOn[] = [];

  // track needs a warehouse to ingest events into — either storage add-on will
  // do, but an account without one gets the usage-based add-on bundled in
  if (!getAnyStorageAddOn(purchase.subscription)) {
    addOns.push({
      product_type: ADDON_PRODUCT_DWH_RENT_TYPE,
      prepaid_units: 0,
    });

    if (areConnectionsAllowed(config)) {
      addOns.push({
        product_type: ADDON_PRODUCT_ETL_CONNECTIONS_TYPE,
        prepaid_units: 1,
      });
    }
  }

  addOns.push({
    product_type: ADDON_PRODUCT_PRODUCT_ANALYTICS_TYPE,
    prepaid_units: 0,
  });

  return addOns;
}

export function getProductAnalyticsRemoveAddOnsData(
  purchase: IPurchase,
): Pick<ICreatePurchaseAddOn, "product_type">[] {
  const addOn = getAnyProductAnalyticsAddOn(purchase.subscription);

  return addOn ? [{ product_type: addOn.product_type }] : [];
}

export interface IProductAnalyticsUsage {
  /** the free variant, which never bills */
  isFree: boolean;
  /** the meter has reported at least once, so the graph has something to draw */
  hasQuota: boolean;
  /** events ingested this billing period */
  usage: number;
  /** lifetime free allowance on the add-on */
  freeUnits: number;
  billableUnits: number;
  includedUnitsUsed: number;
  pricePerEvent: number;
  pricePer1M: number;
  cost: number;
}

/**
 * Metabase Track's usage and cost for the current billing period, or `null`
 * when Track isn't enabled. Only the invoice line item still calls it; the
 * manage card reads the Track descriptors instead.
 *
 * ponytail: duplicates the descriptors' getUsage. GRO-893 wraps the purchase
 * cards in a PurchaseContextProvider and deletes this along with the row.
 */
export function getProductAnalyticsUsage(
  purchase: IPurchase,
): IProductAnalyticsUsage | null {
  const addOn = getAnyProductAnalyticsAddOn(purchase.subscription);

  if (!addOn) {
    return null;
  }

  // the free variant has no Stripe price and no allowance to draw down, so
  // there's nothing to chart and nothing to bill — and the paid fallback rate
  // must not leak into it
  const isFree =
    addOn.product_type === ADDON_PRODUCT_PRODUCT_ANALYTICS_FREE_TYPE;

  const meter = purchase.meters?.[METER_PRODUCT_ANALYTICS_EVENTS_KEY];
  const usage = meter?.meter_value ?? 0;
  const freeUnits = addOn.free_units ?? 0;

  const { billableUnits, includedUnitsUsed } = getPayAsYouGoMeterValues({
    meterValue: usage,
    freeUnits,
    meterFreeUnits: meter?.meter_free_units,
  });

  const pricePerEvent = getProductAnalyticsPricePerEvent(addOn.price_per_unit);

  return {
    isFree,
    hasQuota: typeof meter?.meter_value === "number",
    usage,
    freeUnits,
    billableUnits,
    includedUnitsUsed,
    pricePerEvent,
    pricePer1M: getProductAnalyticsPricePer1M(addOn.price_per_unit),
    cost: isFree ? 0 : billableUnits * pricePerEvent,
  };
}

export function getCreateInstanceData(
  {
    dwh,
    planAlias,
    isSelfHosted = false,
    isYearly = false,
    isDev = false,
    dnsAlias: dns_alias,
    region,
    ai,
    aiFree,
    transforms,
    migrationId: migration_id,
  }: IInstanceSetupData,
  config: Pick<IConfig, "feature_flags">,
): CreateInstanceDataType | null {
  if (planAlias) {
    const plan_alias = buildPlanAlias({
      basePlanAlias: planAlias,
      isSelfHosted,
      isYearly,
      isDev,
    });

    return {
      plan_alias,

      // Cloud only
      dns_alias,
      region,
      add_ons: getCreateInstanceAddOnsData(
        { dwh: dwh, ai, aiFree, transforms },
        config,
      ),

      // For migration
      migration_id,
    };
  } else {
    captureException(new Error("getCreateInstanceData: planObj is undefined"));
  }

  return null;
}

export function getPrettyPlanName({
  subscription,
  product,
}: Pick<IPurchase, "subscription" | "product">): string {
  const isDWHStr = hasDWH({ subscription }) ? " + Storage" : "";
  return `${product.plan.billing_details_name || product.plan.short_name}${isDWHStr}`;
}

export function getPrettyPlanTier(planAlias: PlanAliasType) {
  if (isProPlan(planAlias)) {
    return "Pro";
  }
  if (isStarterPlan(planAlias)) {
    return "Starter";
  }
  if (planAlias === PLAN_ALIAS_INTERNAL) {
    return "Internal";
  }
  return null;
}

export function isCanceledOrDeleted({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    subscription.status === SUBSCRIPTION_STATUS_CANCELLED ||
    hosted_instance?.is_deleted === true ||
    (subscription.is_canceled &&
      subscription.status !== SUBSCRIPTION_STATUS_CANCELLATION_PENDING)
  );
}

export function isBlocked({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    isCanceledOrDeleted({
      subscription,
      hosted_instance,
    }) || hosted_instance?.health !== HEALTH_HEALTHY
  );
}

export function isDeletingDevInstances(
  purchases: IPurchase[],
  purchase: IPurchase,
): boolean {
  const proPurchases = purchases.filter(
    ({ product, subscription }) =>
      !isDevPlan(product.plan.alias) &&
      isProPlan(product.plan.alias) &&
      subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
  );

  const isLastActiveProPlan =
    !proPurchases.length ||
    (proPurchases.length === 1 && proPurchases[0].id === purchase.id);

  const hasDevPurchases = !!purchases.find(
    ({ product, subscription }) =>
      isDevPlan(product.plan.alias) &&
      subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
  );

  return isLastActiveProPlan && hasDevPurchases;
}

// Sort by:
//  - status: trial/active, followed by cancellation-pending, followed by cancelled
//  - within each status, sort by purchase date with recent purchase at the top
export function comparePurchase(
  purchase1: Pick<IPurchase, "subscription" | "hosted_instance">,
  purchase2: Pick<IPurchase, "subscription" | "hosted_instance">,
): number {
  return (
    statusScore(purchase2) - statusScore(purchase1) ||
    purchase2.subscription.created_at.localeCompare(
      purchase1.subscription.created_at,
    )
  );
}

function statusScore({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): number {
  if (
    isCanceledOrDeleted({
      subscription,
      hosted_instance,
    })
  ) {
    return 0;
  } else if (subscription.status === SUBSCRIPTION_STATUS_CANCELLATION_PENDING) {
    return 1;
  } else {
    return 2;
  }
}

// changing plan
// - purchase is not canceled
// - instance is `running` OR self-hosted
// - subscription is `active` OR in `trial`
export function canChangePlan({
  hosted_instance,
  subscription,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    !isCanceledOrDeleted({ hosted_instance, subscription }) &&
    !!(
      (!hosted_instance || hosted_instance.status === STATUS_RUNNING) &&
      (subscription.status === SUBSCRIPTION_STATUS_ACTIVE ||
        subscription.status === SUBSCRIPTION_STATUS_TRIAL)
    )
  );
}

// DWH
export function hasDWH({
  subscription,
}: Pick<IPurchase, "subscription">): boolean {
  const { subscribed_products } = subscription;
  return !!subscribed_products.find(
    ({ product_type }) => product_type === ADDON_PRODUCT_DWH_TYPE,
  );
}

function hasHostingFeature(
  { hosting_features }: Pick<IPurchase, "hosting_features">,
  hostingFeature: string,
): boolean {
  if (hosting_features) {
    return !!(Object.keys(hosting_features).indexOf(hostingFeature) > -1);
  }

  return false;
}

// We allow custom domain only if present in the hosting features list
export function canHaveACustomDomain(
  purchase: Pick<IPurchase, "hosting_features">,
): boolean {
  return hasHostingFeature(purchase, HOSTING_FEATURE_CUSTOM_DOMAIN);
}

// We allow IP allowlisting only if present in the hosting features list
export function canHaveIPAllowlist(
  purchase: Pick<IPurchase, "hosting_features">,
): boolean {
  return hasHostingFeature(purchase, HOSTING_FEATURE_IP_ALLOWLIST);
}

// Can create new production instance?
export function canAccountCreateNewProductionInstance(
  purchases: Pick<IPurchase, "subscription">[],
): boolean {
  const purchasesInTrial = purchases.filter(
    (purchase) => purchase.subscription.in_trial,
  );
  return !purchasesInTrial || (purchasesInTrial && purchasesInTrial.length < 2);
}

// Can create new development instance?
export function canAccountCreateNewDevelopmentInstance(
  purchases: Pick<IPurchase, "product" | "subscription">[],
): boolean {
  const activeProPurchases = purchases
    .filter(({ product }) => isProPlan(product.plan.alias))
    .filter(
      (purchase) => purchase.subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
    );
  return !!activeProPurchases.length;
}

// Enterprise

export function hasAtLeastOneActiveEnterprisePlan(
  purchases: IPurchase[],
): boolean {
  return !!purchases
    .filter(({ product }) => isEnterprisePlan(product.plan.alias))
    .find(
      ({ subscription }) =>
        subscription.status === SUBSCRIPTION_STATUS_TRIAL ||
        subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
    );
}

// Is purchase eligible for pro trial?
export function canTryProPlan(
  purchase: Pick<IPurchase, "hosting_features" | "subscription">,
): boolean {
  return (
    hasHostingFeature(purchase, HOSTING_FEATURE_TRIAL_UP) &&
    purchase.subscription.status === SUBSCRIPTION_STATUS_ACTIVE
  );
}

// Pro trial
export function getProTrial({
  trial_ups,
}: Pick<IPurchase, "trial_ups">): IPurchaseTrialUp | undefined {
  return trial_ups?.find(({ status }) => !!status);
}

// Quota

export function getStorageQuota({
  quota,
}: Pick<IPurchase, "quota">): IQuota | undefined {
  return quota?.[HOSTING_FEATURE_CLICKHOUSE_DWH]
    ? quota[HOSTING_FEATURE_CLICKHOUSE_DWH].rows
    : undefined;
}

export function getMetabotQuota({
  quota,
}: Pick<IPurchase, "quota">): IQuota | undefined {
  return quota?.[HOSTING_FEATURE_METABASE_AI]
    ? quota[HOSTING_FEATURE_METABASE_AI].queries
    : undefined;
}

// Meter

export function areMeterFreeUnitsConsumed({
  meter_free_units: cycleFreeUnits,
  meter_value: cycleUnitsUsed,
}: Pick<IMeter, "meter_free_units" | "meter_value">): boolean {
  // no free units left
  if (typeof cycleFreeUnits === "number" && cycleFreeUnits <= 0) {
    return true;
  }

  // free units is undefined OR null
  // AND units > 0
  if (
    typeof cycleFreeUnits !== "number" &&
    typeof cycleUnitsUsed === "number" &&
    cycleUnitsUsed >= 0
  ) {
    return true;
  }

  // more units used >= free units left
  if (
    typeof cycleFreeUnits === "number" &&
    typeof cycleUnitsUsed === "number"
  ) {
    return cycleUnitsUsed >= cycleFreeUnits;
  }

  return false;
}

export function getTransformsMeterKey(
  productType: AddOnProductType | null | undefined,
): IMeterKey | null {
  switch (productType) {
    case ADDON_PRODUCT_TRANSFORMS_BASIC_METERED_TYPE:
      return METER_TRANSFORMS_BASIC_KEY;
    case ADDON_PRODUCT_TRANSFORMS_ADVANCED_METERED_TYPE:
      return METER_TRANSFORMS_ADVANCED_KEY;
  }
  return null;
}
